import React, { FunctionComponent, useRef } from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import { format } from 'd3-format';
import { map, addIndex, isEmpty, propEq, find } from 'ramda';
import withStyles from 'react-jss';
import { Grid, Popup, Icon, Image } from 'semantic-ui-react';
import classnames from 'classnames';

import DownloadWidget from 'components/DownloadWidget';
import Legend from 'components/Legend';
import { ClassProp } from 'helpers/types';
import noDataLapsedActivity from 'assets/icons/NoData/lapsedactivity.svg';

import {
  lapsedActivityColors,
  PieLabelProps,
  RATIO_CHART_LEGEND,
  sortOrder,
} from '../LapsedActivity.helpers';
import { styles } from '../LapsedActivity.styles';

export type LapsedUser = {
  merchantId: string;
  lapsedUserSegment: string;
  numberOfCls: number;
};

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  height?: number;
  width?: number;
  hasDownload?: boolean;
  isFullPage?: boolean;
  data: LapsedUser[];
  isLarge?: boolean;
  innerRadius?: number;
  outerRadius?: number;
};

const popupContent =
  'Dosh consumers who shopped during the campaign and the amount of time between their previous transaction using 3rd party transaction data';

const mapIndexed = addIndex<any>(map);

const renderHeader = (classes: Styles) => (
  <div className={classes.header}>
    <div>
      <span className={classes.subHeader}>Lapsed Activity</span>

      <Popup
        content={popupContent}
        closeOnTriggerMouseLeave
        hideOnScroll
        size="tiny"
        on="click"
        trigger={<Icon className={classes.icon} name="info circle" />}
      />
    </div>
  </div>
);

const getPoint = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
}: PieLabelProps) => {
  const RADIAN = Math.PI / 180;
  const radius = 25 + innerRadius + (outerRadius - innerRadius);
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return { x, y };
};

const renderCustomizedLabel = ({ cx, percent, index, ...rest }: any) => {
  const { x, y } = getPoint({ ...rest, cx });

  return (
    <text
      x={x}
      y={y}
      fill={'rgba(74,74,74, .8)'}
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
    >
      {format('.0%')(percent)}
    </text>
  );
};

const renderCells = mapIndexed(({ lapsedUserSegment }: any, index: number) => (
  <Cell key={`cell-${index}`} fill={lapsedActivityColors[lapsedUserSegment]} />
));

const renderRadialChart = (
  lapsedUser: LapsedUser[],
  classes: Styles,
  height: number,
  width: number,
  isLarge: boolean,
  innerRadius = 60,
  outerRadius = 120,
) => (
  <>
    <Grid.Row className={classes.radialChart}>
      {isEmpty(lapsedUser) ? (
        <Image src={noDataLapsedActivity} />
      ) : (
        <PieChart width={width} height={height}>
          <Pie
            data={lapsedUser}
            label={renderCustomizedLabel}
            labelLine
            isAnimationActive={false}
            nameKey="lapsedUserSegment"
            dataKey="numberOfCls"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={90}
            endAngle={-270}
          >
            {renderCells(lapsedUser)}
          </Pie>
        </PieChart>
      )}
    </Grid.Row>
    <Grid.Row className={classes.centered}>
      <Legend legend={RATIO_CHART_LEGEND} isLarge={isLarge} />
    </Grid.Row>
  </>
);

const LapsedActivity: FunctionComponent<Props> = ({
  classes,
  data,
  hasDownload = false,
  isFullPage = false,
  height = 300,
  width = 250,
  isLarge = false,
  innerRadius = 60,
  outerRadius = 120,
}) => {
  const contentRef = useRef(null);
  return (
    <>
      {hasDownload && (
        <DownloadWidget
          nodeRef={contentRef}
          className={classes.download}
          header={renderHeader(classes)}
          options={['PNG', 'PDF']}
          imageName="LapsedUsers"
        />
      )}

      <div ref={contentRef}>
        <Grid
          className={classnames({
            [classes.container]: !isFullPage,
            [classes.containerLarge]: isFullPage,
          })}
        >
          {isEmpty(data) ? (
            <Grid.Row className={classes.radialChart}>
              <Image src={noDataLapsedActivity} />
            </Grid.Row>
          ) : (
            renderRadialChart(
              mapOrder(data),
              classes,
              height,
              width,
              isLarge,
              innerRadius,
              outerRadius,
            )
          )}
        </Grid>
      </div>
    </>
  );
};

const mapOrder = (data: LapsedUser[]) =>
  sortOrder.map((value) => find(propEq('lapsedUserSegment', value))(data));

export default withStyles(styles)(LapsedActivity);
